import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Clock, LineChart, Truck, ArrowRight } from 'lucide-react';

const SCENES = [
  { icon: Sparkles, kicker: 'It\u2019s time', title: 'Upgrade your business', text: 'Join the next level of trucking management, driven by AI and built for carriers.' },
  { icon: Truck, kicker: 'Import in seconds', title: 'Amazon trips to settlements', text: 'Drop in your trip history and every load lands on the right truck, driver and week.' },
  { icon: LineChart, kicker: 'Always accurate', title: 'Paystubs that add up', text: 'Gross, dispatch fees, deductions and net pay calculated for you, week over week.' },
  { icon: Clock, kicker: 'Save hours every week', title: 'Focus on what matters', text: 'Less paperwork, fewer calls, more time growing your fleet.' },
];
const BARS = [38, 62, 48, 80, 66, 92, 74];

/** Auto-playing promo "video": cycling scenes with animated chart, captions and progress bar. */
export const PromoReel = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setI((n) => (n + 1) % SCENES.length), 4200);
    return () => clearTimeout(t);
  }, [i]);
  const S = SCENES[i];
  return (
    <section className="py-16 lg:py-24 bg-[#0b1530]" data-testid="promo-reel">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-blue-400/20 bg-gradient-to-br from-[#10224a] via-[#0d1b3d] to-[#091327] shadow-2xl shadow-blue-950/50 aspect-[4/5] sm:aspect-video">
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(96,165,250,.45), transparent 45%)' }} />
          <div className="absolute inset-x-0 sm:left-1/2 bottom-0 flex h-[32%] sm:h-[62%] items-end gap-2 px-6 sm:px-10 pb-14 opacity-70" aria-hidden>
            {BARS.map((h, k) => (
              <div key={k} className="flex-1 rounded-t-md bg-gradient-to-t from-blue-600/60 to-sky-300/70 transition-[height] duration-1000 ease-out"
                style={{ height: `${((h + i * 17 + k * 9) % 70) + 25}%` }} />
            ))}
          </div>
          <div key={i} className="relative z-10 flex h-full flex-col justify-start sm:justify-center p-6 sm:p-12 max-w-xl animate-in fade-in slide-in-from-bottom-4 duration-700">
            <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full bg-blue-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-sky-300" data-testid="promo-kicker">
              <S.icon className="h-4 w-4" /> {S.kicker}
            </span>
            <h2 className="text-3xl sm:text-5xl font-bold leading-tight text-white" data-testid="promo-title">{S.title}</h2>
            <p className="mt-3 text-base sm:text-lg text-blue-100/85">{S.text}</p>
            <Link to="/register" className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-blue-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-400 transition-colors" data-testid="promo-cta">
              Start now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="absolute inset-x-6 sm:inset-x-10 bottom-6 z-10 flex gap-2">
            {SCENES.map((_, k) => (
              <button key={k} type="button" onClick={() => setI(k)} className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/15" aria-label={`Scene ${k + 1}`} data-testid={`promo-dot-${k}`}>
                <span className={`block h-full rounded-full bg-sky-300 ${k === i ? 'animate-[promo-fill_4.2s_linear_forwards]' : k < i ? 'w-full' : 'w-0'}`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
